/**
 * №29 (S1) — the small shared parts of the work rail's four tabs.
 *
 * The probe draws two grammars in the rail and nothing else: a SECTION HEAD
 * (`.chead` — 9.5px, tracked, uppercase) and a CHIP (`.cchip` — a bordered row with
 * a leading mark, a label, and a value hard right). Both live in the one component
 * layer (`app-chrome.css`, R1); this file is just the markup, so all four tabs render
 * the same object rather than four near-identical ones.
 *
 * `RailEmpty` matters more than it looks. Every tab has a real "there is nothing here"
 * case on a thin workspace, and the rule for those is fixed: say what is absent and
 * why, never draw an empty frame that implies data is loading forever, and never
 * invent a row to fill it (D-ST5 / the probe's "every chip is a real record").
 */
import { createContext, useContext, type ReactNode } from "react";

/**
 * №29 (S4.2) — TABS vs STACK, and why one flag is enough.
 *
 * On a conversation the rail is STACKED: every section that has something to say is on
 * screen at once, vertically, and one that has nothing is not there at all. David, twice:
 * *"burying information in tabs SUCKS in a chat… is there relevant context? I can't see
 * it. Is there memory? I can't see it."* A tab is a promise that costs a click to check,
 * and in a conversation you are checking constantly.
 *
 * The whole switch rides on `RailEmpty` because every section already routes its "nothing
 * here" through it — so stacked mode makes an empty section render NOTHING and the section
 * disappears with it (`app-chrome.css`, `.railstack__sec:has(> .railstack__body:empty)`).
 * No section had to be rewritten to learn about stacking, and the honest empty states are
 * NOT deleted: they are exactly what a tab still shows when you go looking for it.
 */
const StackedCtx = createContext(false);

export function RailStacked({ children }: { children: ReactNode }) {
  return <StackedCtx.Provider value={true}>{children}</StackedCtx.Provider>;
}

export function useRailStacked(): boolean {
  return useContext(StackedCtx);
}

export function RailHead({ children }: { children: ReactNode }) {
  return <div className="railhead">{children}</div>;
}

export function RailEmpty({ children }: { children: ReactNode }) {
  // Stacked: an absent section is absent. Explaining what is not there is worth a pane the
  // user chose to open; it is not worth permanent height in a column they are reading.
  if (useRailStacked()) return null;
  return (
    <div className="railempty" data-testid="rail-empty">
      {children}
    </div>
  );
}

export function RailLoading() {
  return (
    <div className="railloading" data-testid="rail-loading" aria-label="Loading">
      <div className="skeleton sk-line" style={{ width: "72%" }} />
      <div className="skeleton sk-line" style={{ width: "90%" }} />
      <div className="skeleton sk-line" style={{ width: "54%" }} />
    </div>
  );
}

/**
 * One bound record. `tone` carries the probe's three chip colours — neutral, money
 * (green mark) and warn (amber mark) — and nothing else: a fourth would be a colour
 * with no meaning attached.
 */
export function RailChip({
  label,
  value,
  tone = "plain",
  title,
  testId,
}: {
  label: ReactNode;
  value?: ReactNode;
  tone?: "plain" | "money" | "warn";
  title?: string;
  testId?: string;
}) {
  return (
    <div className={`railchip railchip--${tone}`} title={title} data-testid={testId ?? "rail-chip"}>
      <span className="railchip__mark" aria-hidden="true" />
      <span className="railchip__label">{label}</span>
      {value != null && <span className="railchip__v">{value}</span>}
    </div>
  );
}
